/**
 * DeepResearchBriefUI
 *
 * Free-text research question → context retrieval → AI pipeline →
 * structured research brief.
 *
 * Engine: pipeline (Airia).  Config note: pipeline_ref and
 * engine_connection_id must be configured via Admin → Tool Registry before
 * this tool is functional. If they're blank the backend will return an error,
 * which we surface clearly.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToolExecution } from '@/hooks/useToolExecution';
import { useUser } from '@/hooks/useUser';
import {
  Search,
  Loader2,
  StopCircle,
  RotateCcw,
  BookOpen,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import type { Tool } from '@/types/database';

interface BriefSection {
  heading: string;
  body: string;
}

interface Citation {
  artifactUrl: string;
  text: string;
  /** Exact source page (from page-backfill), e.g. 14 */
  pageNumber?: number;
}

interface ParsedBrief {
  title?: string;
  sections: BriefSection[];
  citations: Citation[];
}

interface Props {
  tool: Tool;
}

function parseBrief(raw: string): ParsedBrief {
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.sections) return parsed as ParsedBrief;
    if (Array.isArray(parsed?.blocks)) {
      const sections: BriefSection[] = parsed.blocks
        .filter((b: { type: string }) => b.type === 'text')
        .map((b: { title?: string; content: string }) => ({
          heading: b.title ?? '',
          body: b.content,
        }));
      return {
        title: parsed.title,
        sections,
        citations: parsed.citations ?? [],
      };
    }
  } catch {
    // fall through
  }
  // Plain-text fallback
  return { sections: [{ heading: '', body: raw }], citations: [] };
}

export const DeepResearchBriefUI: React.FC<Props> = ({ tool }) => {
  const { user } = useUser();
  const isAdmin = user?.role === 'SYSTEM_ADMIN' || user?.role === 'ORG_OWNER';

  const [question, setQuestion] = useState('');
  const [brief, setBrief] = useState<ParsedBrief | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    executing,
    streamingOutput,
    result,
    error,
    executeTool,
    cancelExecution,
    resetExecution,
  } = useToolExecution();

  // Detect pipeline misconfiguration
  const config = tool.config as Record<string, string> | undefined;
  const pipelineConfigured = !!config?.pipeline_ref;

  useEffect(() => {
    if (executing || (!streamingOutput && !result)) return;
    const raw =
      streamingOutput || (result as { output?: string } | null)?.output || '';
    // Schedule state update outside the effect body to satisfy React Compiler
    if (raw) {
      const parsed = parseBrief(raw);
      setTimeout(() => setBrief(parsed), 0);
    }
  }, [executing, streamingOutput, result]);

  const handleSubmit = async () => {
    const q = question.trim();
    if (!q || executing) return;
    setSubmitted(true);
    setBrief(null);
    resetExecution();
    await executeTool({ toolId: tool.id, input: { question: q } });
  };

  const handleReset = () => {
    setQuestion('');
    setBrief(null);
    setSubmitted(false);
    resetExecution();
  };

  // ── Configuration warning (admin only) ───────────────────────────────────
  const ConfigWarning = isAdmin && !pipelineConfigured && (
    <Card className="border-yellow-300 bg-yellow-50 mb-4">
      <CardContent className="py-3 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-yellow-800">
          Pipeline not configured. Go to <strong>Admin → Tool Registry</strong>,
          edit <em>Deep Research Brief</em>, and set{' '}
          <code className="text-xs bg-yellow-100 px-1 rounded">
            pipeline_ref
          </code>{' '}
          and{' '}
          <code className="text-xs bg-yellow-100 px-1 rounded">
            engine_connection_id
          </code>
          .
        </p>
      </CardContent>
    </Card>
  );

  // ── Results view ─────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="space-y-5">
        {ConfigWarning}

        {/* Question recap + reset */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Search className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 font-medium line-clamp-2">
              {question}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            New question
          </Button>
        </div>

        {/* Streaming progress */}
        {executing && (
          <Card>
            <CardContent className="py-6 flex items-center gap-3 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-sm line-clamp-2">
                {streamingOutput || 'Researching…'}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto text-red-600 border-red-200"
                onClick={cancelExecution}
              >
                <StopCircle className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {error && (
          <Card className="border-red-200">
            <CardContent className="py-4 text-sm text-red-700">
              {error.message}
            </CardContent>
          </Card>
        )}

        {/* Brief */}
        {!executing && brief && (
          <>
            {brief.title && (
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-500" />
                <h2 className="text-xl font-bold text-gray-900">
                  {brief.title}
                </h2>
              </div>
            )}

            {brief.sections.map((section, i) => (
              <Card key={i}>
                {section.heading && (
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {section.heading}
                    </CardTitle>
                  </CardHeader>
                )}
                <CardContent className={section.heading ? '' : 'pt-4'}>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {section.body}
                  </p>
                </CardContent>
              </Card>
            ))}

            {brief.citations.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-600">
                    Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {brief.citations.map((c, i) => (
                      <li key={i}>
                        <a
                          href={c.artifactUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{c.artifactUrl}</span>
                          {typeof c.pageNumber === 'number' && (
                            <span className="ml-auto inline-flex flex-shrink-0 items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                              at p.{c.pageNumber}
                            </span>
                          )}
                        </a>
                        {c.text && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 pl-4">
                            {c.text}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Engine badge — admin only */}
            {isAdmin && (
              <div className="flex justify-end">
                <Badge variant="outline" className="text-xs text-gray-400">
                  {tool.engine ?? tool.type}
                </Badge>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Input view ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {ConfigWarning}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Deep Research Brief
        </h2>
        <p className="text-sm text-gray-500">
          Ask a complex question about county affairs. AgentForge will retrieve
          relevant context from the archive and generate a comprehensive
          research brief.
        </p>
      </div>

      <div className="space-y-2">
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.metaKey) void handleSubmit();
          }}
          placeholder="e.g. What are the county's recent decisions on affordable housing?"
          rows={4}
          className="resize-none text-sm"
          disabled={executing}
        />
        <p className="text-xs text-gray-400">
          Press <kbd className="bg-gray-100 border rounded px-1">⌘ Enter</kbd>{' '}
          or click the button below to generate your brief.
        </p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!question.trim() || executing}
        className="w-full"
      >
        {executing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Researching…
          </>
        ) : (
          <>
            <BookOpen className="h-4 w-4 mr-2" />
            Generate Research Brief
          </>
        )}
      </Button>
    </div>
  );
};
